'use client';

import React, { useState, useCallback, useEffect } from 'react'
import QRCode from "react-qr-code";

import { APIError, useAxios } from '../core/_axios';
import { IpadSetupToken } from '../core/_models';
import useIpadDeviceChannel from '../hooks/useIpadDeviceChannel';
import BaseModal from './_BaseModal';
import { addIpad } from '../core/_requests_v2';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

function ActiveIpadToken({
    ipadToken,
    onIpadConnected = undefined,
    viewingManualSetupInstructions = false
}: {
    ipadToken: IpadSetupToken,
    onIpadConnected?: () => void,
    viewingManualSetupInstructions?: boolean
}) {
    const { ipadIsConnected } = useIpadDeviceChannel({ iPad: ipadToken.ipad });
    const { copy } = useLocalizedCopy();
    const qrCodeContainerDivRef = React.useRef<HTMLDivElement>(null);
    const [qrSize, setQrSize] = React.useState(100);

    const adjustQrSize = React.useCallback(() => {
        if (qrCodeContainerDivRef.current) {
            const height = qrCodeContainerDivRef.current.clientHeight - 30
            setQrSize(Math.min(height, 300));
        }
    }, []);

    React.useEffect(() => {
        adjustQrSize();
        function handleResize() {
            adjustQrSize();
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [adjustQrSize]);

    React.useEffect(() => {
        if (ipadIsConnected) {
            onIpadConnected ? onIpadConnected() : window.location.reload();
        }
    }, [ipadIsConnected, onIpadConnected]);

    if (viewingManualSetupInstructions) {
        return <div>
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                    {copy.IPads.setup.step3}
                </label>
                <div className="mt-4 bg-gray-100 text-gray-900 text-2xl font-mono p-4 rounded-lg text-center">
                    {false ? <>
                        <pre className="font-mono text-lg">
                            O0I1lL9gqQwWvVuU8B6b5S2ZzXxCcPpDdEeFfAaMmNnHhJjKkRrTtYy
                        </pre>
                    </> : <>
                        {ipadToken.short_unique_identifier || 'Invalid Token'}
                    </>}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    {copy.IPads.setup.subtitle}
                </div>
            </div>
        </div>
    }

    return (
        <div>
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                    {copy.IPads.setup.subtitle}
                </label>
            </div>
            <div ref={qrCodeContainerDivRef} className="flex justify-center">
                <div style={{ background: 'white', height: 'calc(100vh - 350px)' }}>
                    <QRCode
                        value={ipadToken.qr_code_url}
                        size={qrSize}
                    />
                </div>
            </div>
        </div>
    )
}

export default function ModalAddIpad({
    addIpadModalIsOpen,
    setAddIpadModalIsOpen,
    ipadSetupToken = undefined,
    onIpadConnected = undefined
}: {
    addIpadModalIsOpen: boolean,
    setAddIpadModalIsOpen: (isOpen: boolean) => void,
    ipadSetupToken?: IpadSetupToken
    onIpadConnected?: () => void
}) {

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    // const [modalIsOpen, setModalIsOpen] = useState(false)

    const [savingIpad, setSavingIpad] = useState(false);
    const [errorSavingIpad, setErrorSavingIpad] = useState<string | null>(null);
    const [successSavingIpad, setSuccessSavingIpad] = useState<string | null>(null);

    const [activeIpadToken, setActiveIpadToken] = useState<IpadSetupToken | null>(ipadSetupToken);

    const [viewingManualSetupInstructions, setViewingManualSetupInstructions] = useState(false);

    const onFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (savingIpad) {
            return;
        }
        setSuccessSavingIpad(null);
        setErrorSavingIpad(null);
        setSavingIpad(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        if (!data.ipad_name) {
            setErrorSavingIpad(copy.Common.messages.requiredField);
            setSavingIpad(false);
            return;
        }
        try {
            const resultingiPadToken = await addIpad(axios, {
                ...data,
            })
            setActiveIpadToken(resultingiPadToken);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
            setErrorSavingIpad(errorMessage)
            setSavingIpad(false)
        }
    }, [axios, savingIpad]);

    useEffect(() => {
        if (!addIpadModalIsOpen) {
            setSuccessSavingIpad(null);
            setErrorSavingIpad(null);
            setSavingIpad(false);
            setActiveIpadToken(null);
            setViewingManualSetupInstructions(false);
        }
    }, [addIpadModalIsOpen]);

    return <BaseModal
        isOpen={addIpadModalIsOpen}
        setIsOpen={setAddIpadModalIsOpen}
        title={copy.IPads.add.title}
        error={errorSavingIpad}
        onFormSubmit={onFormSubmit}
        topRightAction={<>
            {activeIpadToken && <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="leaveOutPdfBooks"
                    className="form-checkbox h-5 w-5 text-primary-600"
                    checked={viewingManualSetupInstructions}
                    onChange={(e) => setViewingManualSetupInstructions(e.target.checked)}
                />
                <label htmlFor="leaveOutPdfBooks" className="text-sm font-medium text-gray-700">
                    {copy.IPads.setup.troubleshooting}
                </label>
            </div>}
        </>}
        actions={activeIpadToken ? <>
            <button
                className="btn btn-sm btn-light me-2"
                type="button"
                onClick={() => {
                    setAddIpadModalIsOpen(false)
                }}
            >
                {copy.Common.buttons.close}
            </button>
        </> : <>
            <button
                className="btn btn-sm btn-light me-2"
                type="button"
                onClick={() => {
                    setAddIpadModalIsOpen(false)
                }}
            >
                {copy.Common.buttons.cancel}
            </button>
            <button
                className="btn btn-sm btn-primary"
                type="submit"
                disabled={savingIpad}
            >
                {!savingIpad ? copy.IPads.add.addButton : copy.IPads.add.adding}
            </button>
        </>}>
        {activeIpadToken ? <>
            <ActiveIpadToken
                ipadToken={activeIpadToken}
                onIpadConnected={onIpadConnected}
                viewingManualSetupInstructions={viewingManualSetupInstructions}
            />
        </> :
            <div>
                <div className="grid gap-7.5">
                    <div className="flex flex-col gap-4">
                        <div>
                            <label
                                htmlFor="ipad_name"
                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                {copy.IPads.add.nameLabel} ({copy.Common.labels.required})
                            </label>
                            <input
                                id="ipad_name"
                                name="ipad_name"
                                type="text"
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                placeholder={copy.IPads.add.namePlaceholder}
                                required
                                autoComplete='off'
                                autoFocus={true}
                                defaultValue={''}
                            />
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                {copy.IPads.add.descriptionPlaceholder}
                            </div>
                        </div>
                    </div>
                    {false && <div className="flex flex-col gap-4">
                        <div>
                            <label
                                htmlFor="ipad_pin"
                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                {copy.IPads.setup.deviceCode} ({copy.Common.labels.required})
                            </label>
                            <input
                                id="ipad_pin"
                                name="ipad_pin"
                                type="text"
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                placeholder="1234"
                                required
                                autoComplete='off'
                                defaultValue={''}
                            />
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                {copy.IPads.setup.subtitle}
                            </div>
                        </div>
                    </div>}
                </div>
            </div>
        }
    </BaseModal>
}
